import { Type } from 'class-transformer';
import { IsArray, IsEnum, IsNumber, IsOptional, IsString, ValidateNested } from 'class-validator';
import {
  PropertyCommonAreaType,
  PropertyCondition,
  PropertyConstructionMaterial,
  PropertyConstructionPeriod,
  PropertyEquipment,
  PropertyExposure,
  PropertyOrientation,
  PropertyParkingSubType,
  PropertyRoofMaterial,
  PropertyRoomType,
  PropertyStanding,
  PropertyView,
} from './property-enums';

export class PropertySpaceDetailDto {
  @IsOptional()
  @IsString()
  id?: string;

  @IsOptional()
  @IsEnum(PropertyRoomType)
  type?: PropertyRoomType;

  @IsOptional()
  @IsNumber()
  surface?: number;

  @IsOptional()
  @IsString()
  precision?: string;

  @IsOptional()
  @IsString()
  lotNumber?: string;
}

export class PropertyConditionsDto {
  @IsOptional()
  @IsEnum(PropertyCondition)
  interior?: PropertyCondition;

  @IsOptional()
  @IsEnum(PropertyCondition)
  commonAreas?: PropertyCondition;
}

export class PropertyInsulationDto {
  @IsOptional()
  @IsString()
  walls?: string;

  @IsOptional()
  @IsString()
  roof?: string;

  @IsOptional()
  @IsString()
  floor?: string;

  @IsOptional()
  @IsString()
  glazing?: string;

  @IsOptional()
  @IsString()
  envelope?: string;
}

export class PropertyConstructionMaterialDto {
  @IsOptional()
  @IsArray()
  @IsEnum(PropertyConstructionMaterial, { each: true })
  walls?: PropertyConstructionMaterial[];

  @IsOptional()
  @IsArray()
  @IsEnum(PropertyRoofMaterial, { each: true })
  roof?: PropertyRoofMaterial[];
}

export class PropertyConstructionDto {
  @IsOptional()
  @IsEnum(PropertyConstructionPeriod)
  year?: PropertyConstructionPeriod;

  @IsOptional()
  @IsString()
  precision?: string;

  @IsOptional()
  @ValidateNested()
  @Type(() => PropertyConstructionMaterialDto)
  material?: PropertyConstructionMaterialDto;

  @IsOptional()
  @IsNumber()
  buildingHeight?: number;

  @IsOptional()
  @IsNumber()
  groundFootprint?: number;

  @IsOptional()
  @IsNumber()
  ceilingHeight?: number;

  @IsOptional()
  @ValidateNested()
  @Type(() => PropertyInsulationDto)
  insulation?: PropertyInsulationDto;

  @IsOptional()
  @IsArray()
  @IsEnum(PropertyOrientation, { each: true })
  orientation?: PropertyOrientation[];

  @IsOptional()
  @IsEnum(PropertyStanding)
  standing?: PropertyStanding;

  @IsOptional()
  @IsArray()
  @IsEnum(PropertyView, { each: true })
  view?: PropertyView[];

  @IsOptional()
  @IsEnum(PropertyExposure)
  exposure?: PropertyExposure;

  @IsOptional()
  @ValidateNested()
  @Type(() => PropertyConditionsDto)
  conditions?: PropertyConditionsDto;

  @IsOptional()
  @IsNumber()
  openings?: number;
}

export class PropertyEquipmentsDto {
  @IsOptional()
  @IsArray()
  @IsEnum(PropertyEquipment, { each: true })
  items?: PropertyEquipment[];
}

export class PropertyParkingDetailDto {
  @IsOptional()
  @IsString()
  id?: string;

  @IsEnum(PropertyParkingSubType)
  type!: PropertyParkingSubType;

  @IsNumber()
  count!: number;

  @IsOptional()
  @IsNumber()
  totalSurface?: number;

  @IsOptional()
  @IsString()
  lotNumber?: string;
}

export class PropertyInteriorDto {
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => PropertySpaceDetailDto)
  rooms: PropertySpaceDetailDto[] = [];

  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => PropertySpaceDetailDto)
  bedroom: PropertySpaceDetailDto[] = [];

  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => PropertySpaceDetailDto)
  bathroom: PropertySpaceDetailDto[] = [];

  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => PropertySpaceDetailDto)
  showerRoom: PropertySpaceDetailDto[] = [];

  @IsOptional()
  @IsNumber()
  wc = 0;
}

export class PropertyExteriorDto {
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => PropertySpaceDetailDto)
  balcony: PropertySpaceDetailDto[] = [];

  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => PropertySpaceDetailDto)
  terrace: PropertySpaceDetailDto[] = [];

  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => PropertySpaceDetailDto)
  garden: PropertySpaceDetailDto[] = [];

  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => PropertySpaceDetailDto)
  loggia: PropertySpaceDetailDto[] = [];

  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => PropertySpaceDetailDto)
  swimmingPool: PropertySpaceDetailDto[] = [];

  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => PropertySpaceDetailDto)
  cellar: PropertySpaceDetailDto[] = [];
}

export class PropertyCommonAreaDetailDto {
  @IsOptional()
  @IsString()
  id?: string;

  @IsEnum(PropertyCommonAreaType)
  type!: PropertyCommonAreaType;

  @IsOptional()
  @IsNumber()
  surface?: number;

  @IsOptional()
  @IsString()
  precision?: string;
}

export class PropertyCharacteristicsDto {
  @ValidateNested()
  @Type(() => PropertyInteriorDto)
  interior: PropertyInteriorDto = new PropertyInteriorDto();

  @IsOptional()
  @ValidateNested()
  @Type(() => PropertyExteriorDto)
  exterior: PropertyExteriorDto = new PropertyExteriorDto();

  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => PropertyCommonAreaDetailDto)
  commonAreas: PropertyCommonAreaDetailDto[] = [];

  @IsOptional()
  @ValidateNested()
  @Type(() => PropertyEquipmentsDto)
  equipments: PropertyEquipmentsDto = new PropertyEquipmentsDto();

  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => PropertyParkingDetailDto)
  parkings?: PropertyParkingDetailDto[];
}
